// What a studio resource is as media, read from its URL. The admin-chosen
// `type` ("Video", "Training Materials") is a free-form group name, not a
// format: three "Prospect Explainer" recordings filed under Training Materials
// are MP3s, and Sabine listed them as videos (SAL-3285). Anything that asks
// "does this play, and as what?" asks here.

import {
  getYouTubeId,
  isDirectVideoUrl,
  isVideoResource,
} from "@/lib/resources/videoThumbnail";

const AUDIO_FILE_RE = /\.(mp3|wav|m4a|aac|flac|ogg|oga)(?:[?#]|$)/i;
const PDF_FILE_RE = /\.pdf(?:[?#]|$)/i;
const OFFICE_FILE_RE = /\.(docx?|pptx?|xlsx?)(?:[?#]|$)/i;
const IMAGE_FILE_RE = /\.(jpe?g|gif|png|webp)(?:[?#]|$)/i;

export const RESOURCE_MEDIA_KIND_LABELS = {
  video: "Video",
  audio: "Audio",
  text: "Written lesson",
  pdf: "PDF",
  document: "Document",
  image: "Image",
  link: "Link",
};

export function isAudioUrl(url = "") {
  return AUDIO_FILE_RE.test(String(url || ""));
}

/** One of the RESOURCE_MEDIA_KIND_LABELS keys. */
export function getResourceMediaKind(resource) {
  const url = resource?.url || "";
  if (resource?.textContent) return "text";
  if (isVideoResource(resource)) return "video";
  if (isAudioUrl(url)) return "audio";
  if (PDF_FILE_RE.test(url)) return "pdf";
  if (OFFICE_FILE_RE.test(url)) return "document";
  if (IMAGE_FILE_RE.test(url)) return "image";
  return "link";
}

/**
 * How a resource plays in place, or null when it is not media: an allowlisted
 * embed, a YouTube video, a video file or an audio file. `ResourcePlayer`
 * renders exactly these four.
 */
export function getInlinePlayback(resource) {
  const url = resource?.url || "";
  if (!url || resource.textContent) return null;
  if (resource.isEmbed) return "embed";
  if (getYouTubeId(url)) return "youtube";
  if (isDirectVideoUrl(url)) return "video";
  if (isAudioUrl(url)) return "audio";
  return null;
}

/** A video or recording someone can play without leaving the page. */
export function isPlayableMedia(resource) {
  const kind = getResourceMediaKind(resource);
  return (kind === "video" || kind === "audio") && Boolean(getInlinePlayback(resource));
}
